import React from 'react';
import { motion } from 'framer-motion';
import { Zap, MapPin, Phone, CheckCircle2 } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Zap,
    title: 'Trigger detected instantly',
    description:
      'Your store fires an event the moment a cart is abandoned, a COD order is placed, or any D2C workflow needs a follow-up — with customer details passed to Neurobloom in real time.',
  },
  {
    number: '02',
    icon: MapPin,
    title: 'Language is identified',
    description:
      "Based on the customer's location, PIN code, and profile, we assign the correct regional language voice — automatically, with no manual configuration needed.",
  },
  {
    number: '03',
    icon: Phone,
    title: 'Voice agent makes the call',
    description:
      'A natural-sounding AI agent calls within minutes — reminding them of what they left, confirming their COD order, or following up on their purchase. It handles questions and nudges toward the next step.',
  },
  {
    number: '04',
    icon: CheckCircle2,
    title: 'Outcome recorded, revenue recovered',
    description:
      "Every call result is logged in your dashboard — full transcript, customer response, and conversion status. You see exactly what is working and what to improve.",
  },
];

const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:sticky lg:top-32"
            >
              <span className="text-[#2563EB] font-semibold text-sm uppercase tracking-wider">
                How it works
              </span>
              <h2
                data-testid="how-it-works-headline"
                className="mt-4 font-['DM_Sans'] text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1C1917] tracking-tight"
              >
                Simple to set up. Powerful in action.
              </h2>
              <p className="mt-4 text-[#57534E] text-base md:text-lg">
                Connect your store, configure your script, and let the voice agent handle
                the rest — 24/7, 365 days a year.
              </p>
            </motion.div>
          </div>

          <div className="lg:col-span-8 space-y-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`step-${index + 1}`}
                className="bg-[#FAFAF9] rounded-3xl p-6 md:p-8 border border-[#E7E5E4] card-hover"
              >
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-[#EFF6FF] rounded-2xl flex items-center justify-center">
                      <step.icon className="w-7 h-7 text-[#2563EB]" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[#2563EB] font-bold text-sm font-['DM_Sans']">
                        {step.number}
                      </span>
                      <h3 className="font-['DM_Sans'] text-xl font-semibold text-[#1C1917]">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-[#57534E] leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
